import {
  fetchFileDownloadMeta,
  getFileDownloadMetaRefetchInterval,
} from "#src/utils/fileDownload";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { type FileDownloadMetaType } from "@validereinc/domain";

export type UseFileDownloadMetaOptions = {
  /** Whether to run the query. Defaults to `true` when a `fileId` is provided. */
  enabled?: boolean;
  /**
   * Milliseconds between scan-status polls while a scan is still pending.
   * Defaults to 1000ms.
   */
  pollSleepTimeMs?: number;
};

/**
 * Fetches a file's download metadata and keeps it fresh via React Query.
 *
 * While the scan is still pending (or a safe file hasn't reached the archived
 * bucket yet), it polls on `pollSleepTimeMs` using React Query's
 * `refetchInterval`. Because polling is expressed as an interval rather than a
 * long-running loop, it stops automatically when the consuming component
 * unmounts — no manual `AbortController` is required. Once the scan is safe, it
 * slows to refreshing the pre-signed URL before it expires; an unsafe file is
 * terminal and stops polling entirely.
 *
 * Note: while scanning, the returned `data` is a non-terminal metadata object
 * (no usable `uri`). Consumers should gate "ready" behaviour on
 * {@link isFileScanComplete}.
 */
export const useFileDownloadMeta = (
  fileId?: string,
  options?: UseFileDownloadMetaOptions
): UseQueryResult<FileDownloadMetaType> =>
  useQuery({
    queryKey: ["files", "meta", fileId],
    queryFn: ({ signal }) => fetchFileDownloadMeta(fileId!, signal),
    refetchInterval: (data) =>
      getFileDownloadMetaRefetchInterval(data, options?.pollSleepTimeMs),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: Boolean(fileId) && (options?.enabled ?? true),
    meta: {
      hideErrorToasts: true,
    },
  });
